// app/game/page.js
'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import Link from 'next/link'
import { useI18n } from '../../components/i18n'
import { openAuthFlow } from '../forum/shared/utils/openAuth'
import {
  getStoredWalletSession,
  hydrateLegacyAuth,
  verifyStoredWalletSession,
} from '../../lib/walletSessionClient'
import HomeBetweenBlocksAd from '../ads'
import { SiteSocialLinksRow } from '../../components/TopBar'
import FooterViewportPark from '../../components/FooterViewportPark'
import FooterMarqueeTail from '../../components/FooterMarqueeTail'

const GAME_SECTOR_BLUEPRINT = [
  { id: 'frontier', code: '01', glyph: '✶', status: 'vision' },
  { id: 'worlds', code: '02', glyph: '◈', status: 'roadmap' },
  { id: 'intelligence', code: '03', glyph: '⬡', status: 'research' },
  { id: 'progress', code: '04', glyph: '✦', status: 'concept' },
  { id: 'community', code: '05', glyph: '⌘', status: 'roadmap' },
  { id: 'economy', code: '06', glyph: '◇', status: 'research' },
  { id: 'access', code: '07', glyph: '⊕', status: 'vision' },
  { id: 'launch', code: '08', glyph: '⟡', status: 'soon' },
]

const GAME_PLATFORM_BLUEPRINT = [
  { key: 'web', src: '/game/web.png' },
  { key: 'ios', src: '/game/ios.png' },
  { key: 'windows', src: '/game/windows.png' },
  { key: 'android', src: '/game/apk.png' },
]


function readCookieValue(name) {
  try {
    if (typeof document === 'undefined') return ''
    const encoded = encodeURIComponent(name)
    const parts = String(document.cookie || '').split(';')
    for (const part of parts) {
      const trimmed = part.trim()
      if (trimmed.startsWith(`${encoded}=`)) {
        return decodeURIComponent(trimmed.slice(encoded.length + 1))
      }
      if (trimmed.startsWith(`${name}=`)) {
        return decodeURIComponent(trimmed.slice(name.length + 1))
      }
    }
  } catch {}
  return ''
}

function storageGet(kind, key) {
  try {
    if (typeof window === 'undefined') return ''
    const store = kind === 'session' ? window.sessionStorage : window.localStorage
    return store?.getItem(key) || ''
  } catch {
    return ''
  }
}

function readMetaStudioAuth() {
  try {
    if (typeof window === 'undefined') return null
    if (
      window.__QL7_AUTH_LOGGED_OUT__ ||
      storageGet('local', 'ql7_auth_logout_lock') ||
      storageGet('session', 'ql7_auth_logout_lock')
    ) {
      return null
    }

    const walletSessionAddress = storageGet('local', 'ql7_wallet_address')
    const walletSessionAccount = storageGet('local', 'ql7_wallet_account_id') || walletSessionAddress
    const walletSessionToken = storageGet('local', 'ql7_wallet_session_token')
    const globalAccount =
      window.__AUTH_ACCOUNT__ ||
      window.__ASHER_ID__ ||
      window.__QL7_UID__ ||
      window.__FORUM_USER__ ||
      window.wallet ||
      window.account ||
      ''
    const accountId = String(
      globalAccount ||
        storageGet('local', 'asherId') ||
        storageGet('local', 'ql7_uid') ||
        storageGet('local', 'forum_user_id') ||
        storageGet('local', 'ql7_account') ||
        storageGet('local', 'account') ||
        storageGet('local', 'wallet') ||
        walletSessionAccount ||
        readCookieValue('asherId') ||
        ''
    ).trim()
    if (!accountId) return null

    return {
      accountId,
      asherId: storageGet('local', 'asherId') || accountId,
      ql7Uid: storageGet('local', 'ql7_uid') || walletSessionAccount,
      forumUserId: storageGet('local', 'forum_user_id'),
      wallet: storageGet('local', 'wallet') || storageGet('local', 'account') || walletSessionAddress || accountId,
      ql7Account: storageGet('local', 'ql7_account') || walletSessionAccount,
      walletSession: Boolean(walletSessionToken),
      vip: storageGet('local', 'ql7_vip') || storageGet('local', 'ai_quota_vip'),
      lang: storageGet('local', 'ql7_lang'),
    }
  } catch {
    return null
  }
}

async function resolveMetaStudioAuth() {
  const direct = readMetaStudioAuth()
  if (direct?.accountId) return direct

  try {
    const stored = getStoredWalletSession()
    const storedAccount = String(stored?.accountId || stored?.walletAddress || '').trim()
    if (!storedAccount) return readMetaStudioAuth()

    if (stored?.token) {
      const verified = await verifyStoredWalletSession()
      if (verified?.authorized && (verified?.accountId || verified?.walletAddress)) {
        return readMetaStudioAuth() || {
          accountId: String(verified.accountId || verified.walletAddress).trim(),
          asherId: String(verified.accountId || verified.walletAddress).trim(),
          ql7Uid: String(verified.accountId || verified.walletAddress).trim(),
          wallet: String(verified.walletAddress || verified.accountId).trim(),
          ql7Account: String(verified.accountId || verified.walletAddress).trim(),
          walletSession: true,
          vip: storageGet('local', 'ql7_vip') || storageGet('local', 'ai_quota_vip'),
          lang: storageGet('local', 'ql7_lang'),
        }
      }
    } else {
      hydrateLegacyAuth({
        accountId: storedAccount,
        walletAddress: stored.walletAddress || storedAccount,
        provider: stored.provider || 'wallet',
      })
      return readMetaStudioAuth()
    }
  } catch (error) {
    console.warn('[metastudio] auth resolve failed', error)
  }

  return readMetaStudioAuth()
}

async function registerMetaStudioInterest(authData) {
  const accountId = String(authData?.accountId || '').trim()
  if (!accountId) throw new Error('missing_account')

  const response = await fetch('/api/metastudio/register', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-forum-user-id': accountId,
    },
    body: JSON.stringify({
      source: 'game-page',
      ...authData,
      accountId,
    }),
  })
  const data = await response.json().catch(() => null)
  if (!response.ok || !data?.ok) {
    throw new Error(data?.error || `HTTP_${response.status}`)
  }
  return data
}

export default function GamePage() {
  const { t } = useI18n()
  const closeTimerRef = useRef(null)
  const supportActionHandledRef = useRef(false)
  const [mounted, setMounted] = useState(false)
  const [metaStudioOpen, setMetaStudioOpen] = useState(false)
  const [metaStudioStatus, setMetaStudioStatus] = useState('idle')
  const [metaStudioNotice, setMetaStudioNotice] = useState('')

  useEffect(() => {
    setMounted(true)
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!metaStudioOpen) return undefined
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && metaStudioStatus !== 'registering') {
        setMetaStudioOpen(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
    }
  }, [metaStudioOpen, metaStudioStatus])

  const gameSectors = GAME_SECTOR_BLUEPRINT.map((sector, index) => ({
    ...sector,
    title: t(`game_premium_sector_${index + 1}_title`),
    summary: t(`game_premium_sector_${index + 1}_summary`),
    paragraphs: String(t(`game_premium_sector_${index + 1}_body`) || '')
      .split(/\n{2,}/)
      .map((item) => item.trim())
      .filter(Boolean),
    statusLabel: t(`game_premium_status_${sector.status}`),
  }))

  const platforms = GAME_PLATFORM_BLUEPRINT.map((platform) => ({
    ...platform,
    label: t(`game_premium_platform_${platform.key}`),
  }))

  const heroAlt = t('game_hero_alt') || 'QGame hero'
  const metaStudioDescription = String(t('metastudio_description') || '')
  const metaStudioParagraphs = metaStudioDescription
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean)

  const openMetaStudio = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    setMetaStudioStatus('idle')
    setMetaStudioNotice('')
    setMetaStudioOpen(true)
  }, [])

  useEffect(() => {
    if (!mounted || supportActionHandledRef.current || typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search || '')
    if (params.get('ql7Action') !== 'metastudio') return
    supportActionHandledRef.current = true
    openMetaStudio()
    try {
      params.delete('ql7Action')
      const nextQuery = params.toString()
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || '#metastudio'}`
      window.history.replaceState(window.history.state, '', nextUrl)
    } catch {}
  }, [mounted, openMetaStudio])

  const closeMetaStudio = useCallback(() => {
    if (metaStudioStatus === 'registering') return
    setMetaStudioOpen(false)
  }, [metaStudioStatus])

  const completeMetaStudioRegistration = useCallback(async (authData) => {
    setMetaStudioOpen(true)
    setMetaStudioStatus('registering')
    setMetaStudioNotice('')
    try {
      await registerMetaStudioInterest(authData)
      setMetaStudioStatus('success')
      setMetaStudioNotice(t('metastudio_success_body'))
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
      closeTimerRef.current = setTimeout(() => {
        setMetaStudioOpen(false)
      }, 5000)
    } catch {
      setMetaStudioStatus('error')
      setMetaStudioNotice(t('metastudio_error'))
    }
  }, [t])

  const handleMetaStudioRegister = useCallback(async () => {
    if (metaStudioStatus === 'registering') return
    const currentAuth = await resolveMetaStudioAuth()
    if (currentAuth?.accountId) {
      await completeMetaStudioRegistration(currentAuth)
      return
    }

    setMetaStudioOpen(false)
    const authAfterFlow = await openAuthFlow({
      readAuth: readMetaStudioAuth,
      timeoutMs: 30000,
    })
    const resolvedAfterFlow = authAfterFlow?.accountId ? authAfterFlow : await resolveMetaStudioAuth()
    if (resolvedAfterFlow?.accountId) {
      await completeMetaStudioRegistration(resolvedAfterFlow)
      return
    }
    setMetaStudioOpen(true)
    setMetaStudioStatus('error')
    setMetaStudioNotice(t('metastudio_auth_required'))
  }, [completeMetaStudioRegistration, metaStudioStatus, t])

  const metaStudioPortal = mounted && metaStudioOpen
    ? createPortal(
        <div className="metastudioOverlay" role="presentation" data-ql7-render-managed="1" data-ql7-visual-scope="overlay">
          <section
            className="metastudioShell"
            role="dialog"
            aria-modal="true"
            aria-labelledby="metastudio-title"
          >
            <button
              type="button"
              className="metastudioClose"
              onClick={closeMetaStudio}
              aria-label={t('metastudio_close_aria')}
              title={t('metastudio_close_aria')}
              disabled={metaStudioStatus === 'registering'}
            >
              &times;
            </button>

            <header className="metastudioHeader">
              <div className="metastudioTitleSvg" id="metastudio-title" aria-label="MetaStudio">
                <svg viewBox="0 0 420 96" role="img" aria-hidden="true">
                  <defs>
                    <linearGradient id="metastudioTitleGradient" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#83f7ff" />
                      <stop offset="34%" stopColor="#c8b7ff" />
                      <stop offset="68%" stopColor="#f8e497" />
                      <stop offset="100%" stopColor="#67ffe1" />
                    </linearGradient>
                    <filter id="metastudioTitleGlow" x="-30%" y="-40%" width="160%" height="180%">
                      <feGaussianBlur stdDeviation="3.2" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <text
                    x="210"
                    y="60"
                    textAnchor="middle"
                    className="metastudioTitleText"
                    fill="url(#metastudioTitleGradient)"
                    filter="url(#metastudioTitleGlow)"
                  >
                    MetaStudio
                  </text>
                  <path className="metastudioTitleRail" d="M72 73 C150 88 270 88 348 73" />
                  <path className="metastudioTitleComet" d="M88 76 C165 60 248 60 336 76" />
                </svg>
              </div>
              <span className="metastudioSoonBadge">{t('metastudio_soon')}</span>
            </header>

            <div className="metastudioPulseRail" aria-hidden="true" />

            <div className="metastudioBody">
              {metaStudioStatus === 'success' ? (
                <div className="metastudioSuccess" aria-live="polite">
                  <h3>{t('metastudio_success_title')}</h3>
                  <p>{metaStudioNotice}</p>
                </div>
              ) : (
                <>
                  <div className="metastudioTextFlow">
                    {metaStudioParagraphs.map((paragraph, index) => (
                      <p key={`${index}-${paragraph.slice(0, 16)}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  {metaStudioNotice ? (
                    <div className="metastudioNotice" aria-live="polite">
                      {metaStudioNotice}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <footer className="metastudioFooter">
              <button
                type="button"
                className="metastudioRegisterBtn"
                onClick={handleMetaStudioRegister}
                disabled={metaStudioStatus === 'registering' || metaStudioStatus === 'success'}
              >
                <span>{metaStudioStatus === 'registering' ? t('metastudio_registering') : t('metastudio_register')}</span>
              </button>
            </footer>
          </section>
        </div>,
        document.body,
      )
    : null

  return (
    <>
      {metaStudioPortal}
      {/* ВНЕШНЯЯ ОБЁРТКА С НАСТРАИВАЕМЫМИ ОТСТУПАМИ */}
      <main className="game-page-root" data-ql7-render-managed="1" data-ql7-visual-scope="route">
        <section className="panel game-panel" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
          {/* РЕСПОНСИВНОЕ ПОЛЕ ИЗ SVG-ГЛИФОВ ПОД ТЕКСТОМ */}
          <ResponsiveGlyphField />

          <div className="game-panel-inner">
    {/*        <h1 className="game-title neon-title">
              <span className="neon-title-core">
                {t('game_title')}
              </span>
              <span
                className="neon-title-glow"
                aria-hidden="true"
              >
                {t('game_title')}
              </span>
            </h1>*/}

            {/* hero-картинка */}
<div className="game-hero-wrap">
  <div
    className="game-hero-bg"
    role="img"
    aria-label={heroAlt}
  />
  <div
    className="game-hero-glow"
    aria-hidden="true"
  />
  <button
    type="button"
    id="metastudio"
    className="gameMetaStudioCta"
    onClick={openMetaStudio}
    aria-label={t('metastudio_open_aria')}
    title={t('metastudio_open_aria')}
  >
    <svg className="gameMetaStudioSvg" viewBox="0 0 250 64" aria-hidden="true">
      <text className="gameMetaWord gameMetaWordQuantum" x="125" y="41" textAnchor="middle">Quantum</text>
      <text className="gameMetaWord gameMetaWordMeta" x="125" y="41" textAnchor="middle">Meta</text>
      <text className="gameMetaWord gameMetaWordStudio" x="125" y="41" textAnchor="middle">Studio</text>
      <path className="gameMetaStudioArc gameMetaStudioArcA" d="M32 47 C78 59 172 59 218 47" />
      <path className="gameMetaStudioArc gameMetaStudioArcB" d="M44 17 C92 7 166 7 207 17" />
      <g className="gameMetaStudioBits">
        <circle cx="52" cy="21" r="1.7" />
        <circle cx="76" cy="50" r="1.35" />
        <circle cx="198" cy="20" r="1.45" />
        <circle cx="218" cy="36" r="1.2" />
        <circle cx="166" cy="52" r="1.25" />
      </g>
    </svg>
  </button>
</div>

            <header className="game-command-deck">
              <div className="game-command-eyebrow">
                <span className="game-command-pulse" aria-hidden="true" />
                {t('game_premium_eyebrow')}
              </div>
              <h1>{t('game_premium_title')}</h1>
              <p>{t('game_premium_intro')}</p>
              <div className="game-command-signals" aria-label={t('game_premium_signal_aria')}>
                <span>{t('game_premium_signal_global')}</span>
                <span>{t('game_premium_signal_responsible')}</span>
                <span>{t('game_premium_signal_player')}</span>
              </div>
            </header>

            <div className="game-sector-grid">
              {gameSectors.map((sector) => (
                <details
                  className="game-sector"
                  data-sector={sector.id}
                  data-ql7-render-managed="1"
                  data-ql7-visual-scope="card"
                  key={sector.id}
                >
                  <summary className="game-sector-summary">
                    <span className="game-sector-code">{sector.code}</span>
                    <span className="game-sector-heading">
                      <span className="game-sector-kicker">
                        <span aria-hidden="true">{sector.glyph}</span>
                        {sector.statusLabel}
                      </span>
                      <strong>{sector.title}</strong>
                      <span className="game-sector-preview">{sector.summary}</span>
                    </span>
                    <span className="game-sector-toggle" aria-hidden="true" />
                  </summary>
                  <div className="game-sector-content">
                    <div className="game-sector-rail" aria-hidden="true" />
                    <div className="game-sector-copy">
                      {sector.paragraphs.map((paragraph, paragraphIndex) => (
                        <p key={`${sector.id}-${paragraphIndex}`}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>

            <aside className="game-disclosure" aria-label={t('game_premium_disclosure_title')}>
              <span className="game-disclosure-mark" aria-hidden="true">i</span>
              <div>
                <strong>{t('game_premium_disclosure_title')}</strong>
                <p>{t('game_premium_disclosure_body')}</p>
              </div>
            </aside>

            <div className="game-ad-slot">
              <HomeBetweenBlocksAd slotKey="ads_between_2_3" slotKind="ads_between" />
            </div>

            <section className="game-platform-zone" aria-labelledby="game-platform-title">
              <div className="game-zone-heading">
                <span>{t('game_premium_platform_eyebrow')}</span>
                <h2 id="game-platform-title">{t('game_premium_platform_title')}</h2>
                <p>{t('game_premium_platform_intro')}</p>
              </div>
              <div className="game-platforms" aria-label={t('game_platforms_aria') || 'Platforms'}>
                {platforms.map(({ key, src, label }) => (
                  <article
                    className="game-platform"
                    data-ql7-render-managed="1"
                    data-ql7-visual-scope="card"
                    key={key}
                  >
                    <span className="game-platform-ribbon">{t('game_premium_status_soon')}</span>
                    <div className="game-platform-orbit">
                      <div className="game-platform-orbit-ring" />
                      <Image
                        src={src}
                        alt={label || t('game_pl') || 'platform'}
                        width={140}
                        height={140}
                        className="game-platform-icon"
                        draggable={false}
                      />
                    </div>
                    <strong className="game-platform-label">{label}</strong>
                    <span className="game-platform-stage">{t('game_premium_platform_stage')}</span>
                  </article>
                ))}
              </div>
            </section>

          </div>

          {/* ЛОКАЛЬНЫЕ СТИЛИ ПАНЕЛИ + ОБЩИЕ ОТСТУПЫ */}
          <style jsx>{`
            /* ---------------------------
               ГЛОБАЛЬНЫЕ НАСТРОЙКИ ОТСТУПОВ
               --------------------------- */

            .game-page-root {
              /* здесь можно настраивать отступы страницы */
              --game-panel-margin-x-mobile: 16px;
              --game-panel-margin-y-mobile: 18px;
              --game-panel-margin-x-desktop: 40px;
              --game-panel-margin-y-desktop: 40px;

  /* full-width, без влияния глобальной .container */
  width: 100%;
  margin: 0 auto;

  padding: var(--game-panel-margin-y-mobile)
    var(--game-panel-margin-x-mobile);
}
            @media (min-width: 900px) {
              .game-page-root {
                padding: var(--game-panel-margin-y-desktop)
                  var(--game-panel-margin-x-desktop);
              }
            }

            .game-panel {
              position: relative;
              overflow: hidden;
              isolation: isolate;
              padding: clamp(14px, 2.4vw, 28px);
              border: 1px solid rgba(237, 198, 87, 0.42);
              border-radius: 34px 11px 34px 11px;
              background:
                radial-gradient(
                  circle at 8% 4%,
                  rgba(255, 190, 52, 0.16),
                  transparent 27%
                ),
                radial-gradient(
                  circle at 94% 20%,
                  rgba(178, 60, 255, 0.2),
                  transparent 31%
                ),
                radial-gradient(
                  circle at 18% 76%,
                  rgba(0, 255, 180, 0.11),
                  transparent 34%
                ),
                radial-gradient(
                  circle at 83% 92%,
                  rgba(255, 35, 124, 0.13),
                  transparent 29%
                ),
                linear-gradient(145deg, #03050b 0%, #07081a 34%, #020b12 68%, #09050f 100%);
              box-shadow:
                inset 0 0 0 1px rgba(92, 240, 255, 0.08),
                inset 0 1px 0 rgba(255, 234, 154, 0.12),
                0 32px 90px rgba(0, 0, 0, 0.96),
                0 0 44px rgba(130, 67, 255, 0.16),
                0 0 28px rgba(236, 188, 62, 0.12);
              --glyph-size-min: 130px;
              --glyph-size-max: 260px;
              --glyph-opacity: 0.95;

              /* центрируем панель, чтобы не липла к краю */
              width: 100%;
              max-width: 1200px;
              margin: 0 auto;
            }

            @media (min-width: 900px) {
              .game-panel {
                padding: 34px 26px 42px;
                --glyph-size-min: 160px;
                --glyph-size-max: 320px;
              }
            }

            .game-panel-inner {
              position: relative;
              z-index: 2;
              display: grid;
              gap: clamp(14px, 2vw, 22px);
              color: #e5f3ff;
              text-shadow: 0 0 8px rgba(15, 23, 42, 0.9);
            }

            .neon-title {
              position: relative;
              display: inline-flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              margin: 0 0 20px;
            }

            .neon-title-core {
              position: relative;
              z-index: 2;
              font-size: clamp(
                1.8rem,
                2.3vw + 1.3rem,
                2.7rem
              );
              letter-spacing: 0.18em;
              text-transform: uppercase;
              background: linear-gradient(
                90deg,
                #fffb01ff,
                #fc7c04ff,
                #00fff2ff,
                #f97316,
                #facc15
              );
              background-size: 220% 100%;
              -webkit-background-clip: text;
              background-clip: text;
              color: transparent;
              animation: titleGradient 10s
                linear infinite;
              text-shadow:
                0 0 16px rgba(15, 23, 42, 0.9),
                0 0 24px rgba(37, 99, 235, 0.75),
                0 0 42px rgba(59, 130, 246, 0.75);
            }

            .neon-title-glow {
              position: absolute;
              inset: 0;
              filter: blur(18px);
              opacity: 0.65;
              color: #60a5fa;
              mix-blend-mode: screen;
              pointer-events: none;
            }

            @keyframes titleGradient {
              0% {
                background-position: 0% 50%;
              }
              50% {
                background-position: 100% 50%;
              }
              100% {
                background-position: 0% 50%;
              }
            }

.game-hero-wrap {
  position: relative;
  width: 100%;
  /* блок сам по себе резиновый, высота от ширины */
  aspect-ratio: 16 / 9;

  border: 1px solid rgba(255, 213, 94, 0.38);
  border-radius: 28px 8px 28px 8px;
  overflow: hidden;
  margin: 0;
  box-shadow:
    0 20px 48px rgba(0, 0, 0, 0.95),
    0 0 40px rgba(37, 99, 235, 0.5),
    0 0 70px rgba(34, 197, 94, 0.35);
}

/* вот это наш "блок под изображение" */
.game-hero-bg {
  position: absolute;
  inset: 0;
  background-image: url('/game/1.png');
  background-size: cover;      /* тянет/ужимает, как надо */
  background-position: center; /* по центру */
  background-repeat: no-repeat;
}

/* glow можно оставить как есть */
.game-hero-glow {
  position: absolute;
  inset: auto 0 -40%;
  height: 55%;
  background:
    radial-gradient(
      ellipse at center,
      rgba(56, 189, 248, 0.35),
      transparent 70%
    ),
    radial-gradient(
      ellipse at center,
      rgba(244, 114, 182, 0.3),
      transparent 80%
    );
  opacity: 0.9;
  mix-blend-mode: screen;
}


            .game-command-deck,
            .game-platform-zone {
              position: relative;
              overflow: hidden;
              border: 1px solid rgba(121, 231, 255, 0.28);
              border-radius: 22px;
              background:
                linear-gradient(135deg, rgba(5, 19, 34, 0.94), rgba(10, 25, 42, 0.82)),
                radial-gradient(circle at 88% 12%, rgba(250, 204, 21, 0.17), transparent 34%);
              box-shadow:
                inset 0 0 0 1px rgba(255, 255, 255, 0.035),
                0 20px 45px rgba(0, 0, 0, 0.42),
                0 0 30px rgba(21, 164, 211, 0.11);
            }

            .game-command-deck {
              display: grid !important;
              grid-template-columns: minmax(0, 1fr) auto;
              align-items: start;
              gap: 13px 22px;
              margin: 0;
              padding: clamp(22px, 4vw, 42px);
            }

            .game-command-deck::after {
              content: '';
              position: absolute;
              inset: 0;
              pointer-events: none;
              background:
                linear-gradient(90deg, transparent, rgba(121, 231, 255, 0.14), transparent) top / 100% 1px no-repeat,
                repeating-linear-gradient(90deg, transparent 0 44px, rgba(121, 231, 255, 0.04) 44px 45px);
              mask-image: linear-gradient(to bottom, #000, transparent 72%);
            }

            .game-command-eyebrow,
            .game-zone-heading > span {
              position: relative;
              z-index: 1;
              display: flex;
              align-items: center;
              gap: 9px;
              color: #f9d66f;
              font-size: 0.72rem;
              font-weight: 900;
              letter-spacing: 0.19em;
              text-transform: uppercase;
              width: fit-content;
              max-width: 100%;
              padding-bottom: 9px;
              line-height: 1.45;
              overflow-wrap: break-word;
            }

            .game-command-eyebrow::after,
            .game-zone-heading > span::after {
              content: '';
              position: absolute;
              inset: auto 0 0;
              width: 100%;
              height: 2px;
              border-radius: 99px;
              background: linear-gradient(90deg, #ff8a2b 0%, #ffd968 28%, #fff1ad 48%, #ff9c31 68%, #ff4d8d 88%, transparent 100%);
              background-size: 180% 100%;
              box-shadow: 0 0 8px rgba(255, 184, 59, 0.78), 0 0 22px rgba(255, 76, 129, 0.25);
              animation: gameEyebrowRail 5.2s ease-in-out infinite;
            }

            @keyframes gameEyebrowRail {
              0%, 100% { background-position: 0% 50%; opacity: .72; }
              48%, 62% { background-position: 100% 50%; opacity: 1; }
            }

            .game-command-pulse {
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #61ffe0;
              box-shadow: 0 0 0 5px rgba(97, 255, 224, 0.1), 0 0 18px #61ffe0;
            }

            .game-command-deck h1,
            .game-zone-heading h2 {
              position: relative;
              z-index: 1;
              margin: 11px 0 10px;
              color: #f8fbff;
              font-size: clamp(1.45rem, 3.4vw, 2.65rem);
              line-height: 1.08;
              letter-spacing: -0.025em;
              text-shadow: 0 0 26px rgba(89, 231, 255, 0.22);
            }

            .game-command-deck h1 { display: block; width: 100%; }

            .game-command-deck h1 {
              grid-column: 1 / -1;
              margin-inline: auto;
              max-width: 980px;
              text-align: center;
              background: linear-gradient(92deg, #fff5bf 0%, #ffffff 22%, #bdfbff 47%, #e0c3ff 70%, #ffd566 100%);
              background-size: 220% 100%;
              -webkit-background-clip: text;
              background-clip: text;
              color: transparent;
              animation: gameTitleSpectrum 11s ease-in-out infinite;
              text-wrap: balance;
              overflow-wrap: break-word;
            }

            @keyframes gameTitleSpectrum {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }

            .game-command-deck > p,
            .game-zone-heading p {
              position: relative;
              z-index: 1;
              max-width: 920px;
              margin: 0;
              color: #cbdbea;
              font-size: clamp(0.92rem, 1.5vw, 1.05rem);
              line-height: 1.75;
            }

            .game-command-deck > p {
              grid-column: 1 / -1;
              justify-self: center;
              text-align: center;
            }

            .game-command-signals {
              position: relative;
              z-index: 1;
              display: grid;
              grid-template-columns: repeat(3, minmax(0, 1fr));
              gap: 18px 10px;
              grid-column: 1 / -1;
              justify-self: center;
              margin: 0 0 8px;
              width: min(880px, 100%);
            }

            .game-command-signals span,
            .game-platform-stage {
              border: 1px solid rgba(97, 255, 224, 0.28);
              border-radius: 999px;
              padding: 7px 11px;
              background: rgba(3, 19, 29, 0.76);
              color: #aefcf0;
              font-size: 0.68rem;
              font-weight: 800;
              letter-spacing: 0.1em;
              text-transform: uppercase;
            }

            .game-command-signals span {
              position: relative;
              isolation: isolate;
              display: flex;
              min-width: 0;
              min-height: 36px;
              align-items: center;
              justify-content: center;
              padding-inline: clamp(7px, 1.4vw, 12px);
              font-size: clamp(0.56rem, 1.05vw, 0.68rem);
              line-height: 1.35;
              letter-spacing: clamp(0.025em, 0.12vw, 0.08em);
              text-align: center;
              overflow-wrap: break-word;
              transform: translateY(-3px);
              box-shadow:
                inset 0 1px 0 rgba(255, 246, 193, 0.16),
                inset 0 -7px 14px rgba(240, 167, 28, 0.045),
                0 7px 0 rgba(66, 43, 7, 0.34),
                0 10px 15px rgba(0, 0, 0, 0.58),
                0 12px 22px rgba(255, 190, 45, 0.2),
                0 18px 34px rgba(255, 135, 25, 0.11);
              animation: gameSignalHover 5.6s ease-in-out infinite;
            }

            .game-command-signals span::before {
              content: '';
              position: absolute;
              left: 9%;
              right: 9%;
              bottom: -15px;
              z-index: -2;
              height: 17px;
              border-radius: 50%;
              background:
                radial-gradient(ellipse at center, rgba(255, 235, 145, 0.86) 0 5%, rgba(255, 192, 48, 0.58) 20%, rgba(255, 111, 20, 0.22) 46%, transparent 74%);
              transform: perspective(90px) rotateX(66deg) scaleX(0.92);
              transform-origin: center;
              box-shadow: 0 0 12px rgba(255, 219, 96, 0.52), 0 0 28px rgba(255, 139, 28, 0.25);
              pointer-events: none;
            }

            .game-command-signals span::after {
              content: '';
              position: absolute;
              inset: 0 13% auto;
              z-index: -1;
              height: 1px;
              background: linear-gradient(90deg, transparent, rgba(255, 243, 187, 0.95), #ffbf3e, transparent);
              box-shadow: 0 0 9px rgba(255, 207, 87, 0.72);
              opacity: 0.82;
              pointer-events: none;
            }

            .game-command-signals span:nth-child(2) { animation-delay: -1.8s; }
            .game-command-signals span:nth-child(3) { animation-delay: -3.6s; }

            @keyframes gameSignalHover {
              0%, 100% { transform: translateY(-3px); }
              50% { transform: translateY(-6px); }
            }

            .game-sector-grid {
              display: grid;
              gap: 16px;
              margin: 0;
            }

            .game-sector {
              --sector-a: 92, 240, 255;
              --sector-b: 255, 205, 79;
              position: relative;
              overflow: hidden;
              isolation: isolate;
              border: 1px solid rgba(var(--sector-a), 0.34);
              border-radius: 25px 7px 25px 7px;
              background:
                radial-gradient(circle at 10% 0, rgba(var(--sector-a), 0.13), transparent 32%),
                radial-gradient(circle at 92% 100%, rgba(var(--sector-b), 0.1), transparent 34%),
                linear-gradient(125deg, rgba(3, 8, 18, 0.985), rgba(8, 12, 29, 0.965));
              box-shadow:
                inset 0 0 0 1px rgba(var(--sector-b), 0.045),
                0 14px 35px rgba(0, 0, 0, 0.42),
                0 0 22px rgba(var(--sector-a), 0.07);
              transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
            }

            .game-sector::before {
              content: '';
              position: absolute;
              inset: 0 0 auto;
              z-index: -1;
              height: 2px;
              background: linear-gradient(90deg, transparent, rgba(var(--sector-a), 0.9) 22%, rgba(var(--sector-b), 0.92) 70%, transparent);
              box-shadow: 0 0 12px rgba(var(--sector-a), 0.46), 0 6px 24px rgba(var(--sector-b), 0.16);
            }

            .game-sector::after {
              content: '';
              position: absolute;
              top: -42px;
              right: 5%;
              z-index: -1;
              width: 210px;
              height: 110px;
              transform: skewX(-28deg);
              background: repeating-linear-gradient(90deg, rgba(var(--sector-a), 0.09) 0 1px, transparent 1px 17px);
              mask-image: linear-gradient(90deg, transparent, #000, transparent);
              opacity: 0.68;
            }

            .game-sector[data-sector='frontier'] { --sector-a: 255, 204, 73; --sector-b: 153, 82, 255; }
            .game-sector[data-sector='worlds'] { --sector-a: 255, 76, 169; --sector-b: 255, 173, 49; }
            .game-sector[data-sector='intelligence'] { --sector-a: 161, 94, 255; --sector-b: 65, 240, 255; }
            .game-sector[data-sector='progress'] { --sector-a: 67, 255, 185; --sector-b: 255, 213, 85; }
            .game-sector[data-sector='community'] { --sector-a: 255, 111, 68; --sector-b: 222, 84, 255; }
            .game-sector[data-sector='economy'] { --sector-a: 255, 212, 82; --sector-b: 62, 255, 211; }
            .game-sector[data-sector='access'] { --sector-a: 64, 194, 255; --sector-b: 113, 106, 255; }
            .game-sector[data-sector='launch'] { --sector-a: 255, 63, 134; --sector-b: 255, 202, 75; }

            .game-sector:hover,
            .game-sector[open] {
              border-color: rgba(var(--sector-b), 0.68);
              box-shadow: 0 18px 42px rgba(0, 0, 0, 0.52), 0 0 30px rgba(var(--sector-a), 0.17), inset 0 0 36px rgba(var(--sector-b), 0.035);
            }

            .game-sector-summary {
              display: grid;
              grid-template-columns: auto minmax(0, 1fr) auto;
              align-items: center;
              gap: clamp(12px, 2.5vw, 22px);
              min-height: 132px;
              padding: clamp(15px, 2.5vw, 24px);
              cursor: pointer;
              list-style: none;
              transition: transform 180ms ease, background 180ms ease;
            }

            .game-sector-summary:hover {
              transform: translateX(3px);
              background: linear-gradient(90deg, rgba(var(--sector-a), 0.035), transparent 48%, rgba(var(--sector-b), 0.025));
            }

            .game-sector-summary::-webkit-details-marker { display: none; }

            .game-sector-summary:focus-visible {
              outline: 2px solid #72f3ff;
              outline-offset: -4px;
              border-radius: 18px;
            }

            .game-sector-code {
              position: relative;
              display: grid;
              place-items: center;
              width: clamp(32px, 4vw, 38px);
              aspect-ratio: 1;
              border: 1px solid rgba(var(--sector-b), 0.68);
              border-radius: 11px 4px 11px 4px;
              background:
                radial-gradient(circle at 35% 25%, rgba(var(--sector-a), 0.34), transparent 28%),
                linear-gradient(145deg, rgba(var(--sector-b), 0.2), rgba(6, 10, 22, 0.96));
              color: rgb(var(--sector-b));
              font-size: 0.62rem;
              font-weight: 900;
              letter-spacing: 0.08em;
              box-shadow:
                inset 0 0 14px rgba(var(--sector-b), 0.14),
                0 0 0 5px rgba(var(--sector-a), 0.09),
                0 0 24px rgba(var(--sector-a), 0.24),
                0 0 22px rgba(var(--sector-b), 0.13);
            }

            .game-sector-code::after {
              content: '';
              position: absolute;
              inset: -7px;
              z-index: -1;
              border-radius: 15px 7px 15px 7px;
              background: radial-gradient(circle, rgba(69, 227, 255, 0.16), transparent 68%);
              filter: blur(4px);
            }

            .game-sector-heading {
              display: grid;
              gap: 6px;
              min-width: 0;
            }

            .game-sector-kicker {
              position: relative;
              display: inline-flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 7px;
              width: fit-content;
              max-width: 100%;
              padding-bottom: 7px;
              color: rgb(var(--sector-a));
              font-size: 0.64rem;
              font-weight: 900;
              letter-spacing: 0.16em;
              line-height: 1.45;
              text-transform: uppercase;
            }

            .game-sector-kicker::after {
              content: '';
              position: absolute;
              inset: auto 0 0;
              width: 100%;
              height: 2px;
              border-radius: 999px;
              background: linear-gradient(90deg, rgb(var(--sector-a)), rgb(var(--sector-b)) 62%, rgba(var(--sector-a), 0.12));
              background-size: 180% 100%;
              box-shadow: 0 0 9px rgba(var(--sector-a), 0.72), 0 0 20px rgba(var(--sector-b), 0.25);
              animation: gameEyebrowRail 5.2s ease-in-out infinite;
            }

            .game-sector-heading strong {
              position: relative;
              width: fit-content;
              max-width: 100%;
              padding-bottom: 7px;
              color: #f4f9ff;
              font-size: clamp(1rem, 2vw, 1.28rem);
              line-height: 1.22;
              text-wrap: balance;
              overflow-wrap: break-word;
            }

            .game-sector-preview {
              max-width: 920px;
              color: #aebfce;
              font-size: clamp(0.8rem, 1.25vw, 0.91rem);
              line-height: 1.62;
              white-space: normal;
              overflow-wrap: break-word;
            }

            .game-sector-toggle {
              position: relative;
              width: 38px;
              height: 38px;
              border: 1px solid rgba(var(--sector-b), 0.52);
              border-radius: 50%;
              background: rgba(11, 25, 37, 0.9);
              box-shadow: 0 0 18px rgba(var(--sector-a), 0.13);
            }

            .game-sector-toggle::before,
            .game-sector-toggle::after {
              content: '';
              position: absolute;
              left: 50%;
              top: 50%;
              width: 13px;
              height: 2px;
              border-radius: 2px;
              background: rgb(var(--sector-b));
              transform: translate(-50%, -50%);
              transition: transform 180ms ease;
            }

            .game-sector-toggle::after { transform: translate(-50%, -50%) rotate(90deg); }
            .game-sector[open] .game-sector-toggle::after { transform: translate(-50%, -50%) rotate(0deg); }
            .game-sector[open] .game-sector-preview { white-space: normal; }

            .game-sector-content {
              display: grid;
              grid-template-columns: 2px minmax(0, 1fr);
              gap: clamp(16px, 3vw, 28px);
              margin: 0 clamp(15px, 3vw, 28px) clamp(18px, 3vw, 30px);
              padding-top: 20px;
              border-top: 1px solid rgba(var(--sector-a), 0.18);
              background: linear-gradient(110deg, rgba(var(--sector-a), 0.025), transparent 45%, rgba(var(--sector-b), 0.025));
              animation: gameSectorReveal 320ms cubic-bezier(.2, .8, .2, 1) both;
            }

            @keyframes gameSectorReveal {
              from { opacity: 0; transform: translateY(-9px); }
              to { opacity: 1; transform: translateY(0); }
            }

            .game-sector-rail {
              border-radius: 999px;
              background: linear-gradient(rgb(var(--sector-a)), rgb(var(--sector-b)) 58%, transparent);
              box-shadow: 0 0 16px rgba(var(--sector-a), 0.44);
            }

            .game-sector-copy {
              display: grid;
              gap: 14px;
            }

            .game-sector-copy p {
              position: relative;
              margin: 0;
              color: #c9d9e7;
              font-size: clamp(0.91rem, 1.4vw, 1rem);
              line-height: 1.78;
            }

            .game-sector-copy p:first-child { color: #e7f5ff; }

            .game-sector-copy p + p {
              margin-top: 3px;
              padding-top: 18px;
              border-top: 1px solid transparent;
              border-image: linear-gradient(90deg, rgba(var(--sector-a), 0.66), rgba(var(--sector-b), 0.2), transparent 88%) 1;
            }

            .game-sector-copy p + p::before {
              content: '';
              position: absolute;
              top: -2px;
              left: 0;
              width: 42px;
              height: 3px;
              border-radius: 99px;
              background: linear-gradient(90deg, rgb(var(--sector-a)), rgb(var(--sector-b)));
              box-shadow: 0 0 9px rgba(var(--sector-a), 0.72), 0 0 19px rgba(var(--sector-b), 0.3);
              animation: gameParagraphPulse 4.8s ease-in-out infinite;
            }

            @keyframes gameParagraphPulse {
              0%, 100% { transform: scaleX(.48); transform-origin: left; opacity: .52; }
              50% { transform: scaleX(1); transform-origin: left; opacity: 1; }
            }

            .game-disclosure {
              display: grid;
              grid-template-columns: auto minmax(0, 1fr);
              gap: 14px;
              margin: 0;
              padding: 17px 18px;
              border: 1px solid rgba(249, 214, 111, 0.3);
              border-radius: 17px;
              background: linear-gradient(120deg, rgba(41, 29, 9, 0.72), rgba(4, 19, 30, 0.9));
            }

            .game-disclosure-mark {
              display: grid;
              place-items: center;
              width: 30px;
              height: 30px;
              border-radius: 50%;
              background: #f9d66f;
              color: #07131f;
              font-weight: 1000;
            }

            .game-disclosure strong { color: #ffe69d; }
            .game-disclosure p { margin: 5px 0 0; color: #afc2d1; font-size: 0.82rem; line-height: 1.58; }

            .game-platform-zone {
              overflow: visible;
              padding: 0;
              border: 0;
              border-radius: 0;
              background: transparent;
              box-shadow: none;
              margin: 0;
            }

            .game-zone-heading {
              position: relative;
              margin: 0;
              padding: clamp(20px, 3vw, 31px) clamp(17px, 3vw, 30px) 22px;
              border: 1px solid rgba(255, 207, 79, 0.28);
              border-bottom: 0;
              border-radius: 24px 7px 0 0;
              background:
                radial-gradient(circle at 12% 0, rgba(255, 197, 62, 0.12), transparent 28%),
                linear-gradient(125deg, rgba(7, 8, 19, 0.97), rgba(12, 7, 23, 0.94));
            }
            .game-zone-heading h2 { font-size: clamp(1.25rem, 2.8vw, 2rem); }

            .game-platforms {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 0;
              overflow: hidden;
              border: 1px solid rgba(255, 207, 79, 0.28);
              border-top-color: rgba(92, 240, 255, 0.15);
              border-radius: 0 0 24px 7px;
              background:
                linear-gradient(90deg, rgba(67, 229, 255, 0.05) 1px, transparent 1px) 0 0 / 34px 100%,
                radial-gradient(circle at 88% 18%, rgba(255, 51, 160, 0.11), transparent 30%),
                linear-gradient(145deg, rgba(3, 11, 22, 0.985), rgba(9, 5, 18, 0.97));
              box-shadow: inset 0 1px 0 rgba(92, 240, 255, 0.08), 0 22px 44px rgba(0, 0, 0, 0.38);
            }

            .game-platform {
              position: relative;
              display: flex;
              min-width: 0;
              flex-direction: column;
              align-items: center;
              gap: 9px;
              overflow: hidden;
              padding: 56px 10px 16px;
              border: 0;
              border-inline-end: 1px solid rgba(112, 221, 255, 0.12);
              border-radius: 0;
              background: transparent;
              text-align: center;
              transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
            }

            .game-platform:hover {
              transform: translateY(-3px);
              background: radial-gradient(circle at 50% 42%, rgba(170, 80, 255, 0.13), transparent 50%);
              box-shadow: inset 0 0 28px rgba(255, 201, 66, 0.035);
            }

            .game-platform:last-child { border-inline-end: 0; }

            .game-platform-ribbon {
              position: absolute;
              top: 11px;
              left: 50%;
              right: auto;
              z-index: 3;
              max-width: calc(100% - 22px);
              overflow: hidden;
              display: flex;
              min-height: 30px;
              align-items: center;
              justify-content: center;
              padding: 6px 11px 6px 13px;
              border: 1px solid rgba(255, 235, 164, 0.74);
              border-radius: 999px 999px 999px 5px;
              background:
                linear-gradient(115deg, #7a5214 0%, #f8d775 34%, #fff2b2 49%, #c58a22 72%, #ffe79a 100%);
              background-size: 220% 100%;
              color: #07131f;
              font-size: clamp(0.5rem, 1vw, 0.62rem);
              font-weight: 1000;
              line-height: 1.2;
              letter-spacing: 0.09em;
              text-align: center;
              text-wrap: balance;
              text-transform: uppercase;
              white-space: normal;
              transform: translateX(-50%);
              box-shadow:
                0 7px 18px rgba(0, 0, 0, 0.42),
                0 0 18px rgba(250, 204, 21, 0.25),
                inset 0 1px 0 rgba(255, 255, 255, 0.55);
              animation: gameRibbonGoldFlow 5.8s ease-in-out infinite;
            }

            .game-platform-ribbon::after {
              content: '';
              position: absolute;
              inset: -80% auto -80% -35%;
              width: 28%;
              transform: rotate(18deg);
              background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.86), transparent);
              animation: gameRibbonGlint 4.6s ease-in-out infinite;
            }

            @keyframes gameRibbonGoldFlow {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }

            @keyframes gameRibbonGlint {
              0%, 58% { left: -38%; opacity: 0; }
              66% { opacity: 0.92; }
              84%, 100% { left: 118%; opacity: 0; }
            }

            .game-platform-orbit {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 8px 0 5px;
            }

            .game-platform-orbit-ring {
              position: absolute;
              width: 126%;
              height: 126%;
              border-radius: 999px;
              border: 1px solid rgba(148, 163, 239, 0.7);
              box-shadow: 0 0 26px rgba(59, 130, 246, 0.5), 0 0 44px rgba(56, 189, 248, 0.34);
              opacity: 0.74;
              transform: rotate(-8deg);
            }

            .game-platform-icon {
              display: block;
              width: min(118px, 23vw);
              height: auto;
              object-fit: contain;
              background: transparent;
              filter: drop-shadow(0 0 12px rgba(59, 130, 246, 0.72)) drop-shadow(0 0 26px rgba(129, 230, 217, 0.48));
              transition: transform 200ms ease, filter 200ms ease;
            }

            .game-platform:hover .game-platform-icon {
              transform: scale(1.05);
              filter: drop-shadow(0 0 16px rgba(56, 189, 248, 0.92)) drop-shadow(0 0 34px rgba(244, 114, 182, 0.62));
            }

            .game-platform-label {
              overflow-wrap: anywhere;
              color: #eef9ff;
              font-size: 0.79rem;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }

            .game-platform-stage { padding: 5px 8px; font-size: 0.56rem; }

            .game-platform-stage {
              width: min(150px, 92%);
              padding: 7px 4px 0;
              border: 0;
              border-top: 1px solid rgba(69, 238, 210, 0.26);
              border-radius: 0;
              background: transparent;
              color: #7de7d5;
              line-height: 1.35;
            }

            .game-ad-slot {
              position: relative;
              z-index: 1;
              overflow: hidden;
              margin: 0;
              border-radius: 18px;
            }

            @media (min-width: 720px) {
              .game-platforms { grid-template-columns: repeat(4, minmax(0, 1fr)); }
            }

            @media (max-width: 600px) {
              .game-panel {
                padding: 22px 14px 26px;
                /* на мобиле глифы в ~2 раза меньше */
                --glyph-size-min: 65px;
                --glyph-size-max: 130px;
              }
              .game-sector-summary {
                grid-template-columns: auto minmax(0, 1fr) auto;
                gap: 9px;
                min-height: 0;
                padding: 15px 11px;
              }
              .game-sector-toggle { width: 32px; height: 32px; }
              .game-sector-preview { white-space: normal; }
              .game-platform { padding-inline: 7px; }
              .game-platform:nth-child(2) { border-inline-end: 0; }
              .game-platform:nth-child(-n + 2) { border-bottom: 1px solid rgba(112, 221, 255, 0.12); }
              .game-platform-icon { width: min(104px, 28vw); }
              .game-command-deck { grid-template-columns: minmax(0, 1fr); padding-inline: 15px; }
              .game-command-eyebrow { justify-self: center; justify-content: center; text-align: center; }
              .game-command-signals { grid-template-columns: minmax(0, 1fr); width: min(330px, 100%); }
              .game-command-signals span { width: 100%; }
              .game-sector-heading strong { font-size: clamp(0.94rem, 4.7vw, 1.12rem); }
            }

            @media (min-width: 601px) and (max-width: 840px) {
              .game-command-signals { grid-template-columns: repeat(2, minmax(0, 1fr)); }
              .game-command-signals span:last-child { grid-column: 1 / -1; justify-self: center; width: min(420px, 100%); }
            }

            @media (prefers-reduced-motion: reduce) {
              .game-command-deck h1,
              .game-command-eyebrow::after,
              .game-command-signals span,
              .game-zone-heading > span::after,
              .game-sector-kicker::after,
              .game-platform-ribbon,
              .game-platform-ribbon::after,
              .game-sector-copy p + p::before,
              .game-sector-content {
                animation: none !important;
              }
            }

            @media (min-width: 1200px) {
              .game-panel {
                --glyph-size-min: 180px;
                --glyph-size-max: 340px;
              }
              .game-sector-grid { grid-template-columns: minmax(0, 1fr); }
            }
          `}</style>
          <style jsx global>{`
            .gameMetaStudioCta {
              position: absolute;
              left: 50%;
              bottom: clamp(14px, 3.2vw, 34px);
              z-index: 4;
              width: min(212px, calc(100% - 34px));
              height: 46px;
              transform: translateX(-50%);
              border: 1px solid rgba(132, 244, 255, 0.72);
              border-radius: 999px;
              cursor: pointer;
              overflow: hidden;
              isolation: isolate;
              background:
                radial-gradient(circle at 18% 35%, rgba(70, 226, 255, 0.22), transparent 34%),
                rgba(3, 16, 28, 0.90);
              box-shadow:
                0 12px 30px rgba(0, 0, 0, 0.58),
                0 0 20px rgba(68, 230, 255, 0.28),
                inset 0 0 0 1px rgba(255, 255, 255, 0.09);
            }

            .gameMetaStudioCta::before {
              content: "";
              position: absolute;
              inset: 0;
              z-index: -1;
              background: linear-gradient(115deg, transparent 0%, rgba(255, 255, 255, 0.24) 46%, transparent 62%);
              transform: translateX(-120%);
              animation: gameMetaButtonSweep 5.6s ease-in-out infinite;
            }

            .gameMetaStudioCta::after {
              content: "";
              position: absolute;
              inset: 7px;
              z-index: -1;
              border-radius: inherit;
              background:
                linear-gradient(rgba(125, 246, 255, 0.16) 1px, transparent 1px),
                linear-gradient(90deg, rgba(247, 226, 140, 0.12) 1px, transparent 1px);
              background-size: 14px 14px;
              mask-image: linear-gradient(90deg, transparent, #000 20%, #000 80%, transparent);
              opacity: 0.36;
            }

            .gameMetaStudioCta:hover,
            .gameMetaStudioCta:focus-visible {
              border-color: rgba(249, 226, 134, 0.9);
              box-shadow:
                0 18px 42px rgba(0, 0, 0, 0.66),
                0 0 30px rgba(68, 230, 255, 0.48),
                0 0 26px rgba(248, 226, 140, 0.34),
                inset 0 0 0 1px rgba(255, 255, 255, 0.13);
              outline: none;
            }

            .gameMetaStudioSvg {
              width: 100%;
              height: 100%;
              display: block;
            }

            .gameMetaWord {
              font-family: inherit;
              font-size: 30px;
              font-weight: 950;
              letter-spacing: 0.02em;
              stroke: rgba(214, 253, 255, 0.62);
              stroke-width: 0.8;
              filter: drop-shadow(0 0 7px rgba(114, 245, 255, 0.74));
              opacity: 0;
              transform-box: fill-box;
              transform-origin: center;
              animation: gameMetaWordCycle 7.2s ease-in-out infinite;
            }

            .gameMetaWordQuantum {
              fill: #eefbff;
            }

            .gameMetaWordMeta {
              fill: #7ff6ff;
              animation-delay: 2.4s;
            }

            .gameMetaWordStudio {
              fill: #f6dd82;
              animation-delay: 4.8s;
            }

            .gameMetaStudioArc {
              fill: none;
              stroke: rgba(127, 246, 255, 0.58);
              stroke-width: 2;
              stroke-linecap: round;
              stroke-dasharray: 52 150;
              animation: gameMetaArcFlow 3.8s ease-in-out infinite;
            }

            .gameMetaStudioArcB {
              stroke: rgba(249, 226, 140, 0.54);
              animation-delay: -1.4s;
            }

            .gameMetaStudioBits circle {
              fill: #f9e68d;
              filter: drop-shadow(0 0 8px rgba(249, 226, 140, 0.85));
              animation: gameMetaBits 2.8s ease-in-out infinite;
            }

            .gameMetaStudioBits circle:nth-child(2),
            .gameMetaStudioBits circle:nth-child(5) {
              fill: #7df6ff;
              animation-delay: -0.9s;
            }

            .gameMetaStudioBits circle:nth-child(3) {
              fill: #c8b7ff;
              animation-delay: -1.6s;
            }

            .metastudioOverlay {
              position: fixed;
              inset: 0;
              z-index: 2147482450;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: calc(18px + env(safe-area-inset-top)) 16px calc(18px + env(safe-area-inset-bottom));
              background:
                radial-gradient(circle at 50% 24%, rgba(56, 189, 248, 0.16), transparent 38%),
                radial-gradient(circle at 72% 64%, rgba(248, 226, 140, 0.13), transparent 34%),
                rgba(2, 5, 14, 0.74);
            }

            .metastudioShell {
              position: relative;
              width: calc(100vw - 32px) !important;
              max-width: 620px !important;
              height: min(760px, calc(100dvh - 48px)) !important;
              max-height: 90vh !important;
              margin-left: auto !important;
              margin-right: auto !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              display: grid;
              grid-template-rows: auto auto minmax(0, 1fr) auto;
              overflow: hidden;
              border-radius: 26px;
              border: 1px solid rgba(109, 229, 255, 0.48);
              background:
                linear-gradient(115deg, rgba(12, 52, 70, 0.62), rgba(3, 9, 22, 0.94) 42%, rgba(70, 58, 14, 0.48)),
                radial-gradient(circle at 18% 12%, rgba(110, 241, 255, 0.24), transparent 38%),
                radial-gradient(circle at 84% 12%, rgba(249, 226, 140, 0.20), transparent 34%);
              box-shadow:
                0 24px 72px rgba(0, 0, 0, 0.72),
                0 0 46px rgba(64, 226, 255, 0.24),
                inset 0 0 0 1px rgba(255, 255, 255, 0.07);
            }

            .metastudioShell::before {
              content: "";
              position: absolute;
              inset: 0;
              pointer-events: none;
              background:
                linear-gradient(rgba(116, 245, 255, 0.065) 1px, transparent 1px),
                linear-gradient(90deg, rgba(249, 226, 140, 0.052) 1px, transparent 1px);
              background-size: 31px 31px;
              mask-image: radial-gradient(circle at 50% 18%, #000 0%, transparent 78%);
              opacity: 0.72;
            }

            .metastudioShell::after {
              content: "";
              position: absolute;
              inset: 1px;
              pointer-events: none;
              border-radius: inherit;
              background:
                linear-gradient(90deg, rgba(103, 232, 249, 0.18), transparent 22%, transparent 78%, rgba(250, 204, 21, 0.15)),
                linear-gradient(180deg, rgba(255, 255, 255, 0.07), transparent 26%);
              mix-blend-mode: screen;
              opacity: 0.76;
            }

            .metastudioClose {
              position: absolute;
              top: 18px;
              right: 18px;
              z-index: 5;
              width: 44px;
              height: 44px;
              border-radius: 999px;
              border: 1px solid rgba(125, 246, 255, 0.54);
              color: #f2fbff;
              background: rgba(3, 15, 29, 0.82);
              box-shadow: 0 0 20px rgba(80, 232, 255, 0.24), inset 0 0 0 1px rgba(255, 255, 255, 0.08);
              font-size: 28px;
              line-height: 1;
              cursor: pointer;
            }

            .metastudioClose:disabled {
              cursor: default;
              opacity: 0.46;
            }

            .metastudioHeader {
              position: relative;
              z-index: 1;
              display: grid;
              place-items: center;
              padding: 38px 70px 14px;
              gap: 6px;
            }

            .metastudioHeader::before,
            .metastudioHeader::after {
              content: "";
              position: absolute;
              left: 22px;
              right: 22px;
              height: 1px;
              background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.26), rgba(250, 204, 21, 0.18), transparent);
              pointer-events: none;
            }

            .metastudioHeader::before {
              top: 16px;
            }

            .metastudioHeader::after {
              bottom: 2px;
            }

            .metastudioTitleSvg {
              width: min(390px, 100%);
              height: 92px;
            }

            .metastudioTitleSvg svg {
              width: 100%;
              height: 100%;
              display: block;
            }

            .metastudioTitleText {
              font-size: 48px;
              font-weight: 950;
              letter-spacing: 0.02em;
              paint-order: stroke;
              stroke: rgba(4, 19, 32, 0.74);
              stroke-width: 4px;
            }

            .metastudioTitleRail,
            .metastudioTitleComet {
              fill: none;
              stroke-linecap: round;
              stroke-width: 2.4;
              stroke-dasharray: 74 170;
              animation: metastudioRailFlow 5.4s ease-in-out infinite;
            }

            .metastudioTitleRail {
              stroke: rgba(125, 246, 255, 0.64);
            }

            .metastudioTitleComet {
              stroke: rgba(249, 226, 140, 0.70);
              animation-delay: -2.2s;
            }

            .metastudioSoonBadge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-width: 76px;
              min-height: 28px;
              padding: 4px 14px;
              border-radius: 999px;
              border: 1px solid rgba(255, 92, 124, 0.72);
              color: #ff6e86;
              background: linear-gradient(90deg, rgba(70, 8, 23, 0.86), rgba(25, 6, 20, 0.88));
              box-shadow: 0 0 18px rgba(255, 72, 110, 0.24);
              font-size: 0.78rem;
              font-weight: 950;
              text-transform: uppercase;
            }

            .metastudioPulseRail {
              position: relative;
              z-index: 1;
              height: 1px;
              margin: 0 28px;
              background: linear-gradient(90deg, transparent, rgba(111, 242, 255, 0.64), rgba(249, 226, 140, 0.74), transparent);
              overflow: hidden;
            }

            .metastudioPulseRail::after {
              content: "";
              position: absolute;
              inset: -2px 0;
              background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.88), transparent);
              transform: translateX(-100%);
              animation: metastudioRailSweep 3.8s ease-in-out infinite;
            }

            .metastudioBody {
              position: relative;
              z-index: 1;
              min-height: 0;
              margin: 18px 22px 0;
              padding: 16px;
              overflow: auto;
              overscroll-behavior: contain;
              border-radius: 22px;
              border: 1px solid rgba(105, 222, 255, 0.34);
              background:
                linear-gradient(140deg, rgba(4, 15, 29, 0.92), rgba(11, 17, 36, 0.90)),
                radial-gradient(circle at 12% 10%, rgba(78, 223, 255, 0.14), transparent 42%);
              box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.045);
              scrollbar-width: thin;
              scrollbar-color: rgba(112, 233, 255, 0.82) rgba(6, 18, 32, 0.8);
            }

            .metastudioBody::before,
            .metastudioBody::after {
              content: "";
              position: sticky;
              display: block;
              height: 1px;
              z-index: 2;
              pointer-events: none;
              background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.42), rgba(250, 204, 21, 0.28), transparent);
              box-shadow: 0 0 16px rgba(34, 211, 238, 0.12);
            }

            .metastudioBody::before {
              top: -16px;
              margin: -2px 6px 14px;
            }

            .metastudioBody::after {
              bottom: -16px;
              margin: 14px 6px -2px;
            }

            .metastudioTextFlow {
              display: grid;
              gap: 13px;
            }

            .metastudioTextFlow p,
            .metastudioSuccess p,
            .metastudioNotice {
              margin: 0;
              color: #e9f8ff;
              font-size: 0.95rem;
              line-height: 1.62;
              white-space: pre-wrap;
              text-shadow: 0 1px 8px rgba(0, 0, 0, 0.62);
            }

            .metastudioTextFlow p {
              position: relative;
              padding: 13px 14px 13px 17px;
              border-radius: 16px;
              border: 1px solid rgba(119, 230, 255, 0.18);
              background:
                linear-gradient(90deg, rgba(93, 233, 255, 0.12), transparent 1px),
                linear-gradient(105deg, rgba(13, 32, 52, 0.62), rgba(7, 12, 27, 0.72));
              box-shadow:
                0 8px 22px rgba(0, 0, 0, 0.20),
                inset 0 0 0 1px rgba(255, 255, 255, 0.035);
            }

            .metastudioTextFlow p::before {
              content: "";
              position: absolute;
              top: -1px;
              left: 18px;
              right: 18px;
              height: 1px;
              background: linear-gradient(90deg, transparent, rgba(249, 226, 140, 0.62), rgba(125, 246, 255, 0.56), transparent);
            }

            .metastudioSuccess {
              min-height: 100%;
              display: grid;
              place-content: center;
              gap: 12px;
              text-align: center;
              padding: 22px;
            }

            .metastudioSuccess::before,
            .metastudioSuccess::after {
              content: "";
              width: min(310px, 72vw);
              height: 1px;
              justify-self: center;
              background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.48), rgba(250, 204, 21, 0.34), transparent);
              box-shadow: 0 0 18px rgba(34, 211, 238, 0.16);
            }

            .metastudioSuccess h3 {
              margin: 0;
              color: #f9e68d;
              font-size: clamp(1.35rem, 3vw, 2rem);
              font-weight: 950;
              text-shadow: 0 0 20px rgba(249, 226, 140, 0.32);
            }

            .metastudioNotice {
              margin-top: 14px;
              padding: 12px 14px;
              border-radius: 16px;
              border: 1px solid rgba(249, 226, 140, 0.38);
              color: #fff0b6;
              background: rgba(72, 52, 10, 0.26);
              text-align: center;
            }

            .metastudioFooter {
              position: relative;
              z-index: 1;
              display: flex;
              justify-content: center;
              padding: 16px 22px 24px;
            }

            .metastudioRegisterBtn {
              min-width: min(280px, 100%);
              min-height: 48px;
              padding: 0 24px;
              border-radius: 999px;
              border: 1px solid rgba(249, 226, 140, 0.78);
              color: #fff7d0;
              cursor: pointer;
              background:
                radial-gradient(circle at 24% 20%, rgba(125, 246, 255, 0.24), transparent 38%),
                linear-gradient(105deg, rgba(14, 92, 89, 0.82), rgba(43, 31, 82, 0.92), rgba(83, 58, 18, 0.84));
              box-shadow:
                0 0 24px rgba(249, 226, 140, 0.28),
                0 12px 32px rgba(0, 0, 0, 0.42),
                inset 0 0 0 1px rgba(255, 255, 255, 0.09);
              font-weight: 950;
              font-size: 0.96rem;
            }

            .metastudioRegisterBtn:disabled {
              cursor: default;
              opacity: 0.62;
            }

            @keyframes gameMetaButtonSweep {
              0%, 42% { transform: translateX(-125%); }
              70%, 100% { transform: translateX(125%); }
            }

            @keyframes gameMetaWordCycle {
              0% { opacity: 0; transform: translateY(14px) scale(0.88); }
              10% { opacity: 1; transform: translateY(0) scale(1); }
              28% { opacity: 1; transform: translateY(0) scale(1); }
              38% { opacity: 0; transform: translateY(-14px) scale(1.08); }
              100% { opacity: 0; transform: translateY(-14px) scale(1.08); }
            }

            @keyframes gameMetaArcFlow {
              0% { stroke-dashoffset: 130; opacity: 0.35; }
              50% { opacity: 1; }
              100% { stroke-dashoffset: -120; opacity: 0.35; }
            }

            @keyframes gameMetaBits {
              0%, 100% { opacity: 0.26; transform: scale(0.74); }
              50% { opacity: 1; transform: scale(1.28); }
            }

            @keyframes metastudioRailFlow {
              0% { stroke-dashoffset: 150; opacity: 0.52; }
              50% { opacity: 1; }
              100% { stroke-dashoffset: -150; opacity: 0.52; }
            }

            @keyframes metastudioRailSweep {
              0%, 32% { transform: translateX(-100%); }
              72%, 100% { transform: translateX(100%); }
            }

            @media (max-width: 760px) {
              .metastudioShell {
                width: min(94vw, 620px) !important;
                max-width: 620px !important;
                height: min(780px, calc(100vh - 32px)) !important;
                height: min(780px, calc(100dvh - 32px)) !important;
              }
            }

            @media (max-width: 640px) {
              .gameMetaStudioCta {
                height: 42px;
                width: min(190px, calc(100% - 28px));
                bottom: 10px;
              }

              .gameMetaWord {
                font-size: 28px;
              }

              .metastudioOverlay {
                --metastudio-mobile-top-offset: 115px;
                align-items: flex-start !important;
                overflow: hidden !important;
                padding:
                  max(var(--metastudio-mobile-top-offset), env(safe-area-inset-top))
                  8px
                  max(12px, env(safe-area-inset-bottom)) !important;
              }

              .metastudioShell {
                width: calc(100vw - 16px) !important;
                max-width: none !important;
                height: min(720px, calc(100vh - var(--metastudio-mobile-top-offset, 56px) - 20px)) !important;
                height: min(720px, calc(100dvh - var(--metastudio-mobile-top-offset, 56px) - 20px)) !important;
                min-height: min(720px, calc(100dvh - var(--metastudio-mobile-top-offset, 56px) - 20px)) !important;
                max-height: min(720px, calc(100dvh - var(--metastudio-mobile-top-offset, 56px) - 20px)) !important;
                border-radius: 22px;
              }

              .metastudioHeader {
                padding: 38px 58px 10px;
              }

              .metastudioTitleText {
                font-size: 42px;
              }

              .metastudioBody {
                margin: 14px 12px 0;
                padding: 12px;
                border-radius: 18px;
              }

              .metastudioFooter {
                padding: 13px 14px 18px;
              }
            }

            @media (prefers-reduced-motion: reduce) {
              .gameMetaStudioCta::before,
              .gameMetaWord,
              .gameMetaStudioArc,
              .gameMetaStudioBits circle,
              .metastudioTitleRail,
              .metastudioTitleComet,
              .metastudioPulseRail::after {
                animation: none !important;
              }

              .gameMetaWordStudio {
                opacity: 1;
                transform: none;
              }
            }
          `}</style>
        </section>
      </main>
      {/* МАРКИЗА (классы как на главной, стили — из globals.css) */}
      <FooterViewportPark scope="game-footer-marquee" reserveMarginTopPx={10}>
        <FooterMarqueeTail renderManaged />
      </FooterViewportPark>

      {/* НИЖНИЕ ИКОНКИ */}
      <FooterViewportPark scope="game-footer-actions">
      <div className="ql7-icons-row ql7-footer-social-layout" data-ql7-render-managed="1" data-ql7-visual-scope="panel">
        <Link
          href="/privacy"
          className="ql7-icon-link ql7-footer-policy-link"
          aria-label="Privacy / Политика"
          style={{ '--size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/policy.png"
            alt="Privacy"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>

        <SiteSocialLinksRow className="ql7-social-links-row--footer" />

        <Link
          href="/forum?ql7SupportOpen=1&inbox=messages&dmUser=ql7-support"
          className="ql7-icon-link ql7-footer-support-link"
          data-ql7-support-entry="1"
          aria-label="Support / Поддержка"
          style={{ '--size': '130px' }}
        >
          <Image
            className="ql7-click-icon"
            src="/click/support.png"
            alt="Support"
            width={130}
            height={130}
            draggable={false}
          />
        </Link>
      </div>
      </FooterViewportPark>
    </>
  )
}

/* ================================
   РЕСПОНСИВНОЕ ПОЛЕ С SVG-ГЛИФАМИ
   ================================ */

function ResponsiveGlyphField() {

  // PNG-глифы из /public/game/glif1.png ... glif10.png
  // у каждого свой слой и базовый размер
  const glyphs = [
    { id: 'glif1', src: '/game/glif1.png', layer: 'front', width: 150, height: 150, intrinsicWidth: 575, intrinsicHeight: 575 },
    { id: 'glif2', src: '/game/glif2.png', layer: 'mid',   width: 500, height: 500, intrinsicWidth: 575, intrinsicHeight: 383 },
    { id: 'glif3', src: '/game/glif3.png', layer: 'mid',   width: 240, height: 240, intrinsicWidth: 360, intrinsicHeight: 360 },
    { id: 'glif4', src: '/game/glif4.png', layer: 'front', width: 280, height: 260, intrinsicWidth: 575, intrinsicHeight: 329 },
    { id: 'glif5', src: '/game/glif5.png', layer: 'mid',   width: 300, height: 300, intrinsicWidth: 1500, intrinsicHeight: 1223 },

    { id: 'glif6', src: '/game/glif6.png', layer: 'back',  width: 410, height: 410, intrinsicWidth: 587, intrinsicHeight: 670 },
    { id: 'glif7', src: '/game/glif7.png', layer: 'back',  width: 450, height: 450, intrinsicWidth: 575, intrinsicHeight: 575 },
    { id: 'glif8', src: '/game/glif8.png', layer: 'mid',   width: 500, height: 500, intrinsicWidth: 575, intrinsicHeight: 383 },
    { id: 'glif9', src: '/game/glif9.png', layer: 'back',  width: 200, height: 200, intrinsicWidth: 575, intrinsicHeight: 494 },
    { id: 'glif10', src: '/game/glif10.png', layer: 'front', width: 100, height: 100, intrinsicWidth: 575, intrinsicHeight: 575 },
  ]


  return (
    <>
      <div
        className="glyph-field"
        aria-hidden="true"
      >
        <div className="glyph-grid">
          {glyphs.map((glyph, index) => (
            <div
              key={glyph.id}
              className={`glyph-item glyph-layer-${glyph.layer}`}
              style={{
                '--glyph-index': index,
                '--glyph-width': `${glyph.width}px`,
                '--glyph-height': `${glyph.height}px`,
              }}
            >
              <Image
                src={glyph.src}
                alt=""
                width={glyph.intrinsicWidth}
                height={glyph.intrinsicHeight}
                priority={glyph.id === 'glif2'}
                className={`glyph-img glyph-img-${glyph.id}`}
                draggable={false}
              />
            </div>
          ))}

        </div>
      </div>

      <style jsx>{`
        .glyph-field {
          position: absolute;
          left: 0;
          right: 0;
          /* начинаем поле примерно под hero-картинкой */
          top: clamp(260px, 32vh, 380px);
          /* позволяем уходить чуть ниже панели, чтобы поле выглядело глубоким */
          bottom: -160px;
          z-index: 0;
          pointer-events: none;
          display: flex;
          align-items: stretch;
          justify-content: center;
        }

        .glyph-grid {
          width: 100%;
          height: 100%;
          display: grid;
          grid-template-columns: repeat(
            auto-fit,
            minmax(
              clamp(
                var(--glyph-size-min),
                30vw,
                var(--glyph-size-max)
              ),
              1fr
            )
          );
          grid-auto-rows: minmax(
            clamp(130px, 30vw, 280px),
            auto
          );
          gap: clamp(10px, 2.2vw, 26px);
          padding: clamp(10px, 2.3vw, 26px);
          opacity: var(--glyph-opacity);
          mix-blend-mode: screen;
          grid-auto-flow: dense;
        }

        .glyph-item {
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(
              0 0 14px rgba(56, 189, 248, 0.55)
            )
            drop-shadow(
              0 0 26px rgba(129, 230, 217, 0.4)
            );
          transform-origin: center center;
          /* большой хаотичный полёт по блоку */
          animation: glyphPathA 34s ease-in-out
            infinite alternate;
          animation-delay: calc(
            var(--glyph-index) * -1.4s
          );
        }

        /* разбиваем на разные траектории,
           чтобы они реально меняли путь */
        .glyph-item:nth-child(3n) {
          animation-name: glyphPathB;
          animation-duration: 38s;
        }

        .glyph-item:nth-child(4n) {
          animation-name: glyphPathC;
          animation-duration: 30s;
        }

        .glyph-item:nth-child(5n) {
          animation-name: glyphPathD;
          animation-duration: 42s;
        }

        .glyph-item:nth-child(7n) {
          animation-name: glyphPathE;
          animation-duration: 36s;
        }

        /* чуть ломаем сетку по высоте */
        .glyph-item:nth-child(5n) {
          grid-row: span 2;
        }

        .glyph-item svg,
        .glyph-item img {
          width: min(var(--glyph-width, 100%), var(--glyph-size-max));
          height: auto;
          max-width: 100%;
          max-height: var(--glyph-height, 520px);
        }

        .glyph-img {
          display: block;
          width: min(var(--glyph-width, 100%), var(--glyph-size-max));
          height: auto;
          max-width: 100%;
          max-height: var(--glyph-height, 520px);
          object-fit: contain;
        }

        .glyph-layer-front {
          opacity: 1;
        }

        .glyph-layer-mid {
          opacity: 0.78;
        }

        .glyph-layer-back {
          opacity: 0.52;
        }

        /* КРУПНЫЕ ТРАЕКТОРИИ.
           Используем vw/vh, чтобы элементы реально
           улетали от одного края блока к другому. */

        @keyframes glyphPathA {
          0% {
            transform: translate3d(
                -4vw,
                -6vh,
                0
              )
              scale(0.85)
              rotate(-4deg);
          }
          20% {
            transform: translate3d(
                14vw,
                -18vh,
                0
              )
              scale(1)
              rotate(3deg);
          }
          40% {
            transform: translate3d(
                26vw,
                4vh,
                0
              )
              scale(1.05)
              rotate(-2deg);
          }
          60% {
            transform: translate3d(
                10vw,
                20vh,
                0
              )
              scale(0.96)
              rotate(2deg);
          }
          80% {
            transform: translate3d(
                -16vw,
                12vh,
                0
              )
              scale(1.02)
              rotate(-3deg);
          }
          100% {
            transform: translate3d(
                -22vw,
                -10vh,
                0
              )
              scale(0.9)
              rotate(0deg);
          }
        }

        @keyframes glyphPathB {
          0% {
            transform: translate3d(
                8vw,
                -12vh,
                0
              )
              scale(0.9)
              rotate(2deg);
          }
          25% {
            transform: translate3d(
                -10vw,
                -6vh,
                0
              )
              scale(1.02)
              rotate(-3deg);
          }
          50% {
            transform: translate3d(
                -20vw,
                18vh,
                0
              )
              scale(1.05)
              rotate(4deg);
          }
          75% {
            transform: translate3d(
                16vw,
                10vh,
                0
              )
              scale(0.95)
              rotate(-2deg);
          }
          100% {
            transform: translate3d(
                22vw,
                -8vh,
                0
              )
              scale(1)
              rotate(0deg);
          }
        }

        @keyframes glyphPathC {
          0% {
            transform: translate3d(
                -18vw,
                10vh,
                0
              )
              scale(0.92)
              rotate(-2deg);
          }
          30% {
            transform: translate3d(
                -4vw,
                -16vh,
                0
              )
              scale(1.03)
              rotate(4deg);
          }
          55% {
            transform: translate3d(
                20vw,
                -4vh,
                0
              )
              scale(1.06)
              rotate(-3deg);
          }
          80% {
            transform: translate3d(
                6vw,
                18vh,
                0
              )
              scale(0.96)
              rotate(3deg);
          }
          100% {
            transform: translate3d(
                -12vw,
                6vh,
                0
              )
              scale(1)
              rotate(0deg);
          }
        }

        @keyframes glyphPathD {
          0% {
            transform: translate3d(
                16vw,
                12vh,
                0
              )
              scale(0.9)
              rotate(0deg);
          }
          20% {
            transform: translate3d(
                -14vw,
                16vh,
                0
              )
              scale(1.02)
              rotate(-4deg);
          }
          45% {
            transform: translate3d(
                -22vw,
                -8vh,
                0
              )
              scale(1.06)
              rotate(4deg);
          }
          75% {
            transform: translate3d(
                12vw,
                -18vh,
                0
              )
              scale(0.95)
              rotate(-3deg);
          }
          100% {
            transform: translate3d(
                6vw,
                4vh,
                0
              )
              scale(1)
              rotate(1deg);
          }
        }

        @keyframes glyphPathE {
          0% {
            transform: translate3d(
                0,
                -18vh,
                0
              )
              scale(0.9)
              rotate(-3deg);
          }
          25% {
            transform: translate3d(
                20vw,
                -6vh,
                0
              )
              scale(1.03)
              rotate(3deg);
          }
          50% {
            transform: translate3d(
                4vw,
                16vh,
                0
              )
              scale(1.05)
              rotate(-2deg);
          }
          75% {
            transform: translate3d(
                -20vw,
                2vh,
                0
              )
              scale(0.96)
              rotate(4deg);
          }
          100% {
            transform: translate3d(
                -6vw,
                -12vh,
                0
              )
              scale(1)
              rotate(0deg);
          }
        }

        @media (max-width: 720px) {
          .glyph-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            grid-auto-rows: minmax(
              clamp(70px, 26vw, 140px),
              auto
            );
            gap: 8px;
            padding: 8px 6px 14px;
          }

          .glyph-item svg,
          .glyph-item img {
            max-height: min(var(--glyph-height, 500px), 500px);
          }


          .glyph-layer-back {
            opacity: 0.34;
          }

          /* на мобиле не удлиняем ряды слишком сильно */
          .glyph-item:nth-child(5n) {
            grid-row: span 1;
          }

          /* делаем полёт поэнергичнее на маленьком экране */
          .glyph-item {
            animation-duration: 26s;
          }
          .glyph-item:nth-child(3n) {
            animation-duration: 28s;
          }
          .glyph-item:nth-child(4n) {
            animation-duration: 24s;
          }
        }

        @media (min-width: 900px) {
          .glyph-item:nth-child(3n + 1) {
            margin-top: -14px;
          }
          .glyph-item:nth-child(3n + 2) {
            margin-top: 6px;
          }
          .glyph-item:nth-child(4n) {
            margin-bottom: 18px;
          }
        }

        @media (min-width: 1200px) {
          .glyph-grid {
            grid-template-columns: repeat(
              auto-fit,
              minmax(clamp(220px, 24vw, var(--glyph-size-max)), 1fr)
            );
            grid-auto-rows: minmax(210px, auto);
          }

          .glyph-item svg,
          .glyph-item img {
            max-height: min(var(--glyph-height, 500px), 500px);
          }


          .glyph-layer-back {
            opacity: 0.58;
          }
        }
      `}</style>
    </>
  )
}

 
